import { useState } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { MATERIALS, SHUTTER_STYLES, INITIAL_FORM_DATA } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

export const useShutterQuote = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [quoteResult, setQuoteResult] = useState(null);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.email && formData.phone && formData.postcode;
      case 2:
        return formData.shutterStyle && formData.material;
      case 3:
        return formData.windows.some(w => w.width && w.height);
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      calculateQuote();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const addWindow = () => {
    const newId = formData.windows.length + 1;
    updateFormData('windows', [...formData.windows, { 
      id: newId, 
      width: '', 
      height: '', 
      label: '',
      windowType: 'flat'
    }]);
  };

  const removeWindow = (id) => {
    if (formData.windows.length > 1) {
      updateFormData('windows', formData.windows.filter(w => w.id !== id));
    }
  };

  const updateWindow = (id, field, value) => {
    updateFormData('windows', formData.windows.map(w => 
      w.id === id ? { ...w, [field]: value } : w
    ));
  };

  const calculateQuote = async () => {
    setIsLoading(true);
    
    const material = MATERIALS.find(m => m.id === formData.material);
    const basePricePerSqm = material?.pricePerSqm || 250;
    const bayWindowPremium = material?.bayWindowPremium || 20;
    
    let totalArea = 0;
    let totalBayArea = 0;
    const windowDetails = [];
    
    formData.windows.forEach(w => {
      if (w.width && w.height) {
        let widthMm = parseFloat(w.width);
        let heightMm = parseFloat(w.height);
        
        if (formData.unit === 'cm') {
          widthMm *= 10;
          heightMm *= 10;
        }
        
        const areaSqm = (widthMm * heightMm) / 1000000;
        totalArea += areaSqm;
        
        const isBay = w.windowType === 'bay';
        if (isBay) {
          totalBayArea += areaSqm;
        }
        
        windowDetails.push({
          label: w.label || `Window ${windowDetails.length + 1}`,
          width: widthMm,
          height: heightMm,
          area: areaSqm,
          windowType: w.windowType || 'flat',
          isBayWindow: isBay
        });
      }
    });
    
    // Calculate price: base price + bay window premium for bay windows
    const regularPrice = totalArea * basePricePerSqm;
    const bayPremiumTotal = totalBayArea * (bayWindowPremium / 1.2); // Premium is inc VAT, convert to ex VAT
    const basePrice = regularPrice + bayPremiumTotal;
    const vatAmount = basePrice * 0.2;
    const totalPrice = basePrice + vatAmount;
    
    const quoteData = {
      windows: windowDetails,
      totalArea: totalArea.toFixed(2),
      totalBayArea: totalBayArea.toFixed(2),
      style: SHUTTER_STYLES.find(s => s.id === formData.shutterStyle),
      material: material,
      pricePerSqm: basePricePerSqm,
      bayWindowPremium: bayWindowPremium,
      basePrice: basePrice.toFixed(2),
      vatAmount: vatAmount.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
      customerName: formData.name,
      customerEmail: formData.email,
      customerPhone: formData.phone,
      customerPostcode: formData.postcode
    };
    
    try {
      await axios.post(`${API}/api/quotes/send-estimate-email`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_postcode: formData.postcode,
        shutter_style: quoteData.style?.name,
        material: quoteData.material?.name,
        installation_type: 'Full Service',
        windows: windowDetails,
        total_area_sqm: totalArea.toFixed(2),
        price_per_sqm: basePricePerSqm,
        subtotal: basePrice.toFixed(2),
        vat: vatAmount.toFixed(2),
        total_price: totalPrice.toFixed(2)
      });
      
      toast({
        title: "Quote Sent!",
        description: "We've emailed your estimate to " + formData.email,
      });
    } catch (error) {
      console.error('Error sending email:', error);
    }
    
    setQuoteResult(quoteData);
    setCurrentStep(4);
    setIsLoading(false);
  };

  const resetQuote = () => {
    setFormData(INITIAL_FORM_DATA);
    setQuoteResult(null);
    setCurrentStep(1);
  };

  return {
    currentStep,
    setCurrentStep,
    isLoading,
    formData,
    quoteResult,
    updateFormData,
    canProceed,
    handleNext,
    handleBack,
    addWindow,
    removeWindow,
    updateWindow,
    resetQuote
  };
};
